import { type ReactNode, type Ref, useId } from 'react';
import {
  Checkbox as AriaCheckbox,
  CheckboxGroup as AriaCheckboxGroup,
  type CheckboxGroupProps as AriaCheckboxGroupProps,
  type CheckboxProps as AriaCheckboxProps,
  type CheckboxRenderProps,
} from 'react-aria-components';

import type { FixedControlSize } from '@/lib/base/forms/control-size';
import type {
  SelectionCardSize,
  SelectionCardVariant,
} from '@/lib/base/forms/selection-card-shared';
import { selectionCardClassName } from '@/lib/base/forms/selection-card-shared';
import { cx } from '@/utils/cx';

/** Box, corner radius, and glyph dimensions per size. */
const CHECKBOX_SIZES: Record<
  FixedControlSize,
  { box: string; indeterminate: string; check: string }
> = {
  sm: { box: 'size-4 rounded', indeterminate: 'h-3 w-2.5', check: 'size-3' },
  md: {
    box: 'size-5 rounded-md',
    indeterminate: 'size-3.5',
    check: 'size-3.5',
  },
  lg: { box: 'size-6 rounded-md', indeterminate: 'size-4', check: 'size-4' },
};

export interface CheckboxBaseProps {
  size?: FixedControlSize;
  className?: string;
  isFocusVisible?: boolean;
  isSelected?: boolean;
  isDisabled?: boolean;
  isIndeterminate?: boolean;
}

export const CheckboxBase = ({
  className,
  isSelected,
  isDisabled,
  isIndeterminate,
  size = 'sm',
  isFocusVisible = false,
}: CheckboxBaseProps) => {
  return (
    <div
      className={cx(
        'relative flex shrink-0 cursor-pointer appearance-none items-center justify-center bg-primary ring-1 ring-primary ring-inset',
        CHECKBOX_SIZES[size].box,
        (isSelected || isIndeterminate) && 'bg-brand-solid ring-bg-brand-solid',
        isDisabled && 'cursor-not-allowed bg-disabled_subtle ring-disabled',
        isFocusVisible && 'outline-2 outline-offset-2 outline-focus-ring',
        className
      )}
    >
      <svg
        aria-hidden='true'
        viewBox='0 0 14 14'
        fill='none'
        className={cx(
          'pointer-events-none absolute text-fg-white opacity-0 transition-inherit-all',
          CHECKBOX_SIZES[size].indeterminate,
          isIndeterminate && 'opacity-100',
          isDisabled && 'text-fg-disabled_subtle'
        )}
      >
        <path
          d='M2.91675 7H11.0834'
          stroke='currentColor'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>

      <svg
        aria-hidden='true'
        viewBox='0 0 14 14'
        fill='none'
        className={cx(
          'pointer-events-none absolute text-fg-white opacity-0 transition-inherit-all',
          CHECKBOX_SIZES[size].check,
          isSelected && !isIndeterminate && 'opacity-100',
          isDisabled && 'text-fg-disabled_subtle'
        )}
      >
        <path
          d='M11.6666 3.5L5.24992 9.91667L2.33325 7'
          stroke='currentColor'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    </div>
  );
};
CheckboxBase.displayName = 'CheckboxBase';

interface CheckboxProps extends AriaCheckboxProps {
  ref?: Ref<HTMLLabelElement>;
  size?: FixedControlSize;
  label?: ReactNode;
  hint?: ReactNode;
  /** Accessible helper text associated to the control via aria-describedby. Unlike `hint` (visual-only), this is announced by screen readers. */
  description?: ReactNode;
}

export const Checkbox = ({
  label,
  hint,
  description,
  size = 'sm',
  className,
  ...ariaCheckboxProps
}: CheckboxProps) => {
  const descId = useId();
  const describedBy =
    [ariaCheckboxProps['aria-describedby'], description ? descId : undefined]
      .filter(Boolean)
      .join(' ') || undefined;
  const sizes: Record<
    FixedControlSize,
    { root: string; textWrapper: string; label: string; hint: string }
  > = {
    sm: {
      root: 'gap-2',
      textWrapper: '',
      label: 'text-sm font-medium',
      hint: 'text-sm',
    },
    md: {
      root: 'gap-3',
      textWrapper: 'gap-0.5',
      label: 'text-md font-medium',
      hint: 'text-md',
    },
    lg: {
      root: 'gap-3',
      textWrapper: 'gap-0.5',
      label: 'text-lg font-medium',
      hint: 'text-md',
    },
  };

  return (
    <AriaCheckbox
      {...ariaCheckboxProps}
      aria-describedby={describedBy}
      className={(
        state: CheckboxRenderProps & { defaultClassName: string | undefined }
      ) =>
        cx(
          'flex items-start',
          state.isDisabled && 'cursor-not-allowed',
          sizes[size].root,
          typeof className === 'function' ? className(state) : className
        )
      }
    >
      {({
        isSelected,
        isIndeterminate,
        isDisabled,
        isFocusVisible,
      }: {
        isSelected: boolean;
        isIndeterminate: boolean;
        isDisabled: boolean;
        isFocusVisible: boolean;
      }) => (
        <>
          <CheckboxBase
            size={size}
            isSelected={isSelected}
            isIndeterminate={isIndeterminate}
            isDisabled={isDisabled}
            isFocusVisible={isFocusVisible}
            className={label || hint || description ? 'mt-0.5' : ''}
          />
          {(label || hint || description) && (
            <div
              className={cx('inline-flex flex-col', sizes[size].textWrapper)}
            >
              {label && (
                <p
                  className={cx(
                    'text-secondary select-none',
                    sizes[size].label
                  )}
                >
                  {label}
                </p>
              )}
              {hint && (
                <span
                  className={cx('text-tertiary', sizes[size].hint)}
                  onClick={event => event.preventDefault()}
                >
                  {hint}
                </span>
              )}
              {description && (
                <p
                  id={descId}
                  className={cx('text-tertiary', sizes[size].hint)}
                >
                  {description}
                </p>
              )}
            </div>
          )}
        </>
      )}
    </AriaCheckbox>
  );
};
Checkbox.displayName = 'Checkbox';

interface CheckboxGroupProps extends AriaCheckboxGroupProps {
  children: ReactNode;
  className?: string;
}

export const CheckboxGroup = ({
  children,
  className,
  ...props
}: CheckboxGroupProps) => {
  return (
    <AriaCheckboxGroup
      {...props}
      className={cx('flex flex-col gap-3', className)}
    >
      {children}
    </AriaCheckboxGroup>
  );
};
CheckboxGroup.displayName = 'CheckboxGroup';

export interface CheckboxCardProps extends AriaCheckboxProps {
  /** Card contents (icon, title, details, …). */
  children?: ReactNode;
  /**
   * Visual treatment. `'card'` (default) is the bordered card; `'button'`
   * renders each option pixel-identical to a secondary Button.
   */
  variant?: SelectionCardVariant;
  /** Card size. Only meaningful for the `'button'` variant. Defaults to 'md'. */
  size?: SelectionCardSize;
}

/**
 * A selectable card backed by a checkbox — the multi-select counterpart to
 * {@link RadioCard}, sharing one visual implementation via
 * {@link selectionCardClassName} so the two can never drift apart. The whole
 * card is the click target.
 *
 * react-aria renders a visually-hidden native `<input type='checkbox'>` inside
 * the card, so `getByRole` queries, keyboard interaction, and assistive-tech
 * announcements work without any per-caller ARIA wiring.
 *
 * Use inside a {@link CheckboxGroup} for any "pick several of N cards" UI.
 *
 * @example
 * <CheckboxGroup value={selected} onChange={setSelected} aria-label='Pick sources'>
 *   <CheckboxCard value='a'>…card content…</CheckboxCard>
 *   <CheckboxCard value='b'>…card content…</CheckboxCard>
 * </CheckboxGroup>
 */
export const CheckboxCard = ({
  children,
  className,
  variant = 'card',
  size = 'md',
  ...ariaCheckboxProps
}: CheckboxCardProps) => {
  return (
    <AriaCheckbox
      {...ariaCheckboxProps}
      className={renderProps =>
        cx(
          selectionCardClassName(variant, size, renderProps),
          variant === 'card' && 'flex-col',
          typeof className === 'function' ? className(renderProps) : className
        )
      }
    >
      {children}
    </AriaCheckbox>
  );
};
CheckboxCard.displayName = 'CheckboxCard';
